import { useState } from "react";
import { Plus, Check } from "lucide-react";

const DEFAULT_TABS = ["Rapport instantané", "Rapports précédents", "Rapports planifiés"];
const AGG = [["none", "Aucun"], ["min", "min"], ["max", "max"], ["sum", "somme"], ["avg", "moyenne"], ["count", "nombre"]];
const INP = "w-full bg-secondary/60 border border-border rounded-md px-2.5 py-1.5 text-sm outline-none focus:border-[#17A2B8]";

export default function ConfigPanel({ title, criteriaFields, values, setValues, columns, setColumns, units = [], onOK, onAdd, tabs = DEFAULT_TABS, addLabel = "Nouveau", extraContent = {} }) {
  const [tab, setTab] = useState(0);
  const setV = (k, v) => setValues((s) => ({ ...s, [k]: v }));
  const toggleCol = (i) => setColumns((cs) => cs.map((c, idx) => (idx === i ? { ...c, enabled: !c.enabled } : c)));
  const setAgg = (i, v) => setColumns((cs) => cs.map((c, idx) => (idx === i ? { ...c, agg: v } : c)));

  return (
    <div className="border border-border rounded-lg bg-card overflow-hidden mb-5" data-testid="config-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <h2 className="font-bold text-lg">{title}</h2>
        {onAdd && (
          <button onClick={onAdd} data-testid="config-add" className="flex items-center gap-1 bg-[#17A2B8] hover:bg-[#1391a5] text-white text-xs font-semibold px-3 py-1.5 rounded-md transition-colors">
            <Plus className="w-4 h-4" /> {addLabel}
          </button>
        )}
      </div>

      <div className="flex border-b border-border text-xs font-semibold overflow-x-auto">
        {tabs.map((t, i) => (
          <button key={t} onClick={() => setTab(i)} data-testid={`config-tab-${i}`}
            className={`px-4 py-2 whitespace-nowrap ${tab === i ? "bg-[#17A2B8]/15 text-[#2ec5db] border-b-2 border-[#17A2B8]" : "text-muted-foreground hover:text-foreground"}`}>{t}</button>
        ))}
      </div>

      {tab !== 0 ? (
        extraContent[tab] ? (
          <div className="p-4">{extraContent[tab]}</div>
        ) : (
          <div className="p-6 text-sm text-muted-foreground">
            {tab === 1 ? "Historique des exécutions précédentes — bientôt disponible." : "Planification récurrente (quotidienne / hebdomadaire) — bientôt disponible."}
          </div>
        )
      ) : (
        <>
          <div className="p-4 grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <p className="label-mono mb-2">Critère</p>
              <div className="space-y-2.5">
                {criteriaFields.map((f) => (
                  <div key={f.key} className="grid grid-cols-[130px_1fr] items-start gap-2">
                    <label className="text-xs text-muted-foreground pt-1.5">{f.label}{f.suffix ? ` (${f.suffix})` : ""}</label>
                    {f.type === "checkbox" ? (
                      <input type="checkbox" className="mt-2" checked={!!values[f.key]} onChange={(e) => setV(f.key, e.target.checked)} data-testid={`crit-${f.key}`} />
                    ) : f.type === "select" ? (
                      <select className={INP} value={values[f.key] || ""} onChange={(e) => setV(f.key, e.target.value)} data-testid={`crit-${f.key}`}>
                        {f.options.map((o) => <option key={o[0]} value={o[0]}>{o[1]}</option>)}
                      </select>
                    ) : f.type === "units" ? (
                      <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto border border-border rounded-md p-2 bg-secondary/40" data-testid={`crit-${f.key}`}>
                        {units.length === 0 && <span className="text-xs text-muted-foreground">Aucune unité</span>}
                        {units.map((u) => {
                          const on = (values[f.key] || []).includes(u.value);
                          return (
                            <button type="button" key={u.value} data-testid={`unit-chip-${u.value}`}
                              onClick={() => setV(f.key, on ? (values[f.key] || []).filter((x) => x !== u.value) : [...(values[f.key] || []), u.value])}
                              className={`text-[11px] px-2 py-1 rounded-full border transition-colors ${on ? "bg-[#17A2B8] text-white border-[#17A2B8]" : "border-border text-muted-foreground hover:border-[#17A2B8]"}`}>{u.label}</button>
                          );
                        })}
                      </div>
                    ) : (
                      <input type={f.type} className={INP} value={values[f.key] ?? ""} onChange={(e) => setV(f.key, e.target.value)} data-testid={`crit-${f.key}`} />
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div>
              <p className="label-mono mb-2">Colonnes</p>
              <div className="border border-border rounded-md divide-y divide-border max-h-64 overflow-y-auto">
                {columns.map((c, i) => (
                  <div key={c.key} className="flex items-center gap-2 px-3 py-1.5 text-sm" data-testid={`col-${c.key}`}>
                    <input type="checkbox" checked={c.enabled} onChange={() => toggleCol(i)} data-testid={`col-toggle-${c.key}`} />
                    <span className="flex-1 truncate">{c.label}</span>
                    {c.numeric && (
                      <select className="bg-secondary/60 border border-border rounded px-1.5 py-0.5 text-xs" value={c.agg || "none"} onChange={(e) => setAgg(i, e.target.value)} data-testid={`col-agg-${c.key}`}>
                        {AGG.map((a) => <option key={a[0]} value={a[0]}>{a[1]}</option>)}
                      </select>
                    )}
                  </div>
                ))}
                {columns.length === 0 && <div className="px-3 py-4 text-xs text-muted-foreground">Aucune colonne configurable.</div>}
              </div>
            </div>
          </div>
          <div className="px-4 py-3 border-t border-border">
            <button onClick={onOK} data-testid="config-ok" className="bg-[#17A2B8] hover:bg-[#1391a5] text-white font-semibold px-6 py-2 rounded-md text-sm flex items-center gap-1"><Check className="w-4 h-4" /> OK</button>
          </div>
        </>
      )}
    </div>
  );
}
